import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { companiesApi, type Company } from "@/api/companies";
import { queryKeys } from "./queryKeys";

type CompanyContextValue = {
  company: Company | null;
  isLoading: boolean;
  error: Error | null;
};

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: async () => {
      const result = await companiesApi.list();
      const list = Array.isArray(result) ? result : (result.companies ?? []);
      // ND opera com 1 company internamente — pega a primeira ativa
      const active = list.find((c) => c.status === "active") ?? list[0] ?? null;
      return active;
    },
    staleTime: 60_000,
  });

  return (
    <CompanyContext.Provider
      value={{ company: data ?? null, isLoading, error: (error as Error) ?? null }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used inside CompanyProvider");
  return ctx;
}

export function useCompanyId(): string | null {
  return useContext(CompanyContext)?.company?.id ?? null;
}
